import { FC, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import { confirmPlan } from '../../plans/services/PlanCRUD';
import { copyToClipboard } from '../../../utils/copyToClipboard';
import {
  QuickSalePlanModel,
  QuickSaleUserModel,
} from '../models/QuickSaleModel';
import { apiErrorMessage, formatEuro } from '../utils/quickSaleFormat';

type Props = {
  user: QuickSaleUserModel;
  plan: QuickSalePlanModel;
  onClose: () => void;
  onConfirmed: () => Promise<void> | void;
};

/**
 * Closing the sale of a proposal plan. With a saved card the plan is confirmed
 * here, which charges it like the plan page does; without one, the customer
 * pays through the checkout link. The proposal orders are the ones the backend
 * already generated: nothing here creates orders of its own.
 */
const FinalizeModal: FC<Props> = ({ user, plan, onClose, onConfirmed }) => {
  const intl = useIntl();
  const [confirming, setConfirming] = useState(false);
  const card = user.bankcard;
  const firstOrder = plan.orders.pending[0];

  async function onConfirm() {
    setConfirming(true);
    try {
      await confirmPlan(plan.id);
    } catch (error) {
      toast.error(
        apiErrorMessage(
          error,
          intl.formatMessage({ id: 'quickSale.confirmError' })
        )
      );
      setConfirming(false);
      return;
    }
    toast.success(intl.formatMessage({ id: 'quickSale.planConfirmed' }));
    // Closing first: the reload replaces the plan this modal was opened for.
    onClose();
    await onConfirmed();
  }

  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton>
        <h4 className="mb-0">
          {intl.formatMessage({ id: 'quickSale.finalize' })}
        </h4>
      </Modal.Header>
      <Modal.Body>
        {card ? (
          <p className="fs-5 mb-0">
            {intl.formatMessage(
              { id: 'quickSale.chargeCard' },
              {
                amount: formatEuro(intl, firstOrder?.price?.total),
                card: card.is_paypal
                  ? 'PayPal'
                  : `${card.card_type || ''} ···· ${card.last_4_digits || ''}`,
              }
            )}
          </p>
        ) : (
          <>
            <p className="fs-5">
              {intl.formatMessage({ id: 'quickSale.noCardCheckout' })}
            </p>
            {plan.checkout_url ? (
              <div className="input-group">
                <input
                  className="form-control"
                  readOnly
                  value={plan.checkout_url}
                  aria-label={intl.formatMessage({
                    id: 'quickSale.checkoutLink',
                  })}
                />
                <button
                  type="button"
                  className="btn btn-light-primary"
                  onClick={() =>
                    copyToClipboard({
                      text: plan.checkout_url,
                      successMessage: intl.formatMessage({
                        id: 'quickSale.linkCopied',
                      }),
                    })
                  }
                >
                  <i className="fas fa-copy me-1" />
                  {intl.formatMessage({ id: 'quickSale.copy' })}
                </button>
              </div>
            ) : (
              <div className="text-danger">
                {intl.formatMessage({ id: 'quickSale.noCheckoutLink' })}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          {intl.formatMessage({
            id: card ? 'quickSale.cancel' : 'quickSale.close',
          })}
        </Button>
        {card && (
          <Button variant="success" disabled={confirming} onClick={onConfirm}>
            {confirming && (
              <span className="spinner-border spinner-border-sm me-2" />
            )}
            {intl.formatMessage({ id: 'quickSale.confirmAndCharge' })}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export { FinalizeModal };
